// @file Wrapper for Cognito related API

import 'isomorphic-fetch';
import * as CognitoIdentity from 'amazon-cognito-identity-js';
import estimateLanguage from '../lib/estimate-language';
import mixpanel from 'mixpanel-browser';
import signupReferrer from '../lib/signup-referrer';

// ビルド時に値へ置き換えるので、process.env を分割代入せずに 1 つずつ参照する
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USERPOOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
} as CognitoIdentity.ICognitoUserPoolData;
export const userPool = new CognitoIdentity.CognitoUserPool(poolData);

export const signUp = (username: string, email: string, password: string) =>
  new Promise<CognitoIdentity.ISignUpResult>((resolve, reject) => {
    // NOTE: if we have more language option, let's extend
    const locale = estimateLanguage();
    const referrer = signupReferrer();
    const attributeList = [
      new CognitoIdentity.CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoIdentity.CognitoUserAttribute({
        Name: 'locale',
        // 今使っている言語を送信する
        Value: locale,
      }),
      // ユーザ分析のために、リファラーを送信する
      new CognitoIdentity.CognitoUserAttribute({
        Name: 'custom:signup_tag',
        Value: referrer,
      }),
    ];

    userPool.signUp(username, password, attributeList, [], (err, result) => {
      if (err || !result) {
        return reject(err);
      }
      resolve(result);
    });
  });

export const requestVerificationCode = (identity: string) => {
  const userPool = new CognitoIdentity.CognitoUserPool(poolData);

  return new Promise((resolve, reject) => {
    const userData = {
      Username: identity,
      Pool: userPool,
    };

    const cognitoUser = new CognitoIdentity.CognitoUser(userData);
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
      } else {
        resolve(true);
      }
    });
  });
};

export const verify = (username: string, code: string) =>
  new Promise((resolve, reject) => {
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoIdentity.CognitoUser(userData);
    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });

export const signin = (username: string, password: string) =>
  new Promise<{
    cognitoUser: CognitoIdentity.CognitoUser;
    session: CognitoIdentity.CognitoUserSession;
    accessToken: string;
  }>((resolve, reject) => {
    const cognitoUser = new CognitoIdentity.CognitoUser({
      Username: username,
      Pool: userPool,
    });
    const authenticationDetails = new CognitoIdentity.AuthenticationDetails({
      Username: username,
      Password: password,
    });
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const accessToken = result.getIdToken().getJwtToken();
        cognitoUser.getSession(
          (err: any, session: CognitoIdentity.CognitoUserSession) => {
            if (err) {
              reject(err);
            } else {
              resolve({ cognitoUser, session, accessToken });
            }
          },
        );
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });

export let currentSession: CognitoIdentity.CognitoUserSession | undefined;

export const getSession = () =>
  new Promise<CognitoIdentity.CognitoUserSession | null>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser === null) {
      return resolve(null);
    }

    cognitoUser.getSession((err: Error | null, session: CognitoIdentity.CognitoUserSession | null) => {
      if (err || !session) {
        cognitoUser.signOut();
        return reject(err);
      }

      currentSession = session;
      resolve(session);
    });
  });

export const refreshSession = (session: CognitoIdentity.CognitoUserSession) => {
  return new Promise<CognitoIdentity.CognitoUserSession>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser === null) {
      reject(new Error('no session found'));
      return;
    }

    // We don't need to refresh the session if it is valid
    if (session.isValid() === true) {
      return resolve(session);
    }
    const refreshToken = session.getRefreshToken();
    cognitoUser.refreshSession(
      refreshToken,
      (err: Error, session: CognitoIdentity.CognitoUserSession) => {
        if (err) {
          cognitoUser.signOut();
          return reject(err);
        }
        currentSession = session;
        return resolve(session);
      },
    );
  });
};

export const signout: () => Promise<void> = () =>
  new Promise<void>((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    mixpanel.reset();

    // Persists `geolonia__persisted*` items
    // e.g. `geolonia__persisted_language` will be used at signin page without session
    const thePersisted = Object.keys(localStorage)
      .filter((key) => key.indexOf('geolonia__persisted') === 0)
      .reduce<{ [key: string]: string }>((prev, key) => {
      const value = localStorage.getItem(key);
      if (value) {
        prev[key] = value;
      }
      return prev;
    }, {});

    localStorage.clear();

    Object.keys(thePersisted).forEach((key) => {
      const value = thePersisted[key];
      localStorage.setItem(key, value);
    });
    resolve();
  });

/**
 *
 * @param identity username or email
 */
export const sendVerificationEmail: (identity: string) => Promise<boolean> = (identity: string) =>
  new Promise((resolve, reject) => {
    const cognitoUser = new CognitoIdentity.CognitoUser({
      Username: identity,
      Pool: userPool,
    });
    if (cognitoUser) {
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(true),
        onFailure: (err) => {
          reject(err);
        },
      });
    }
  });

/**
 *
 * @param identity username or email
 */
export const resetPassword = (
  identity: string,
  code: string,
  password: string,
) => {
  const cognitoUser = new CognitoIdentity.CognitoUser({
    Username: identity,
    Pool: userPool,
  });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, password, {
      onFailure(err) {
        reject(err);
      },
      onSuccess() {
        resolve(true);
      },
    });
  });
};

export const changePassword = (oldPassword: string, newPassword: string) =>
  new Promise<void>((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      const username = cognitoUser.getUsername();
      signin(username, oldPassword)
        .then(({ cognitoUser }) => {
          cognitoUser.changePassword(oldPassword, newPassword, (err) => {
            if (err) {
              reject(err);
            } else {
              resolve();
            }
          });
        })
        .catch((err) => reject(err));
    } else {
      reject(new Error('No user found.'));
    }
  });

// NOTE: not tested
export const changeEmail = (email: string) => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      const attr: CognitoIdentity.ICognitoUserAttributeData = {
        Name: 'email',
        Value: email,
      };
      cognitoUser.updateAttributes([attr], (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    } else {
      reject();
    }
  });
};
